import { createGeoError, getCurrentLocation } from "@/utils/map";
import { closeToast, showConfirmDialog, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";

const NAV_LOCATION_TIMEOUT_MS = 5000;
/** 高于地图标记弹框（mobile 1999 / pc 5000），避免定位提示被遮挡 */
const NAV_OVERLAY_Z_INDEX = 6000;

const readOriginFromPosition = (position) => {
  const lat = Number(position?.coords?.latitude);
  const lng = Number(position?.coords?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

const raceTimeout = (promise, ms, localeOptions = {}) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(
        () => reject(createGeoError("timeout", localeOptions, { code: 3, timedOut: true })),
        ms
      );
    }),
  ]);

/** MapTiler 导航：5s 内获取起点；失败则询问是否继续（不带起点） */
export function useMapTilerNavigationGeolocation() {
  const { t, locale } = useI18n();

  const confirmContinueWithoutOrigin = () =>
    showConfirmDialog({
      title: t("app.home.map_nav_location_unavailable_title"),
      message: t("app.home.map_nav_location_unavailable_message"),
      confirmButtonText: t("app.home.map_nav_continue"),
      cancelButtonText: t("app.home.map_nav_cancel"),
      zIndex: NAV_OVERLAY_Z_INDEX,
    })
      .then(() => true)
      .catch(() => false);

  const requestOriginForNavigation = async () => {
    showLoadingToast({
      message: t("app.home.map_nav_getting_location"),
      duration: 0,
      forbidClick: false,
      zIndex: NAV_OVERLAY_Z_INDEX,
    });

    try {
      const localeOptions = { locale: locale.value };
      const position = await raceTimeout(
        getCurrentLocation({
          preferCache: true,
          quick: true,
          timeout: NAV_LOCATION_TIMEOUT_MS,
          locale: locale.value,
        }),
        NAV_LOCATION_TIMEOUT_MS,
        localeOptions
      );
      const origin = readOriginFromPosition(position);
      if (origin) {
        return {
          origin,
          navigationAfterAsync: !position?.fromCache,
          cancelled: false,
        };
      }
    } catch {
      // 超时或定位不可用，走确认弹窗
    } finally {
      closeToast();
    }

    const confirmed = await confirmContinueWithoutOrigin();
    if (!confirmed) {
      return { origin: null, navigationAfterAsync: true, cancelled: true };
    }

    return { origin: null, navigationAfterAsync: true, cancelled: false };
  };

  return { requestOriginForNavigation };
}
